import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Vietnamese mirror of the spouse-visa document checklist guide. */
const TITLE = "Giấy tờ Thái Lan cho visa vợ/chồng — danh mục và thứ tự chứng nhận";
const DESC =
  "Hồ sơ visa vợ/chồng cần những giấy tờ Thái Lan nào, thứ tự dịch và chứng nhận đúng, cùng những lỗi thường dẫn đến bị trả lại.";
const PATH = "/blog/spouse-visa-document-checklist";
const URL = `/vi${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "Hồ sơ visa vợ/chồng cần những giấy tờ Thái Lan nào?",
    answer:
      "Thường gồm giấy đăng ký kết hôn và giấy chứng nhận kết hôn, giấy khai sinh, bản sao hộ khẩu, chứng minh nhân dân hoặc hộ chiếu, và tuỳ nước có thể thêm lý lịch tư pháp cùng giấy tờ chứng minh tài chính.",
  },
  {
    question: "Dịch trước hay chứng nhận trước?",
    answer:
      "Chứng nhận trước rồi mới dịch, để bản dịch bao gồm cả các con dấu và dòng chú thích được thêm ở các bước sau.",
  },
  {
    question: "Giấy tờ hộ tịch có thời hạn không?",
    answer:
      "Nhiều cơ quan yêu cầu bản cấp trong vòng 1–6 tháng. Hãy xin giấy tờ theo lịch nộp, không xin quá sớm.",
  },
  {
    question: "Kết hôn ở nước ngoài thì có phải ghi chú tại Thái Lan không?",
    answer:
      "Nhiều trường hợp cần đăng ký ghi chú tại cơ quan hộ tịch Thái Lan để giấy tờ Thái Lan thể hiện đúng tình trạng hôn nhân; hãy xác nhận với cơ quan tiếp nhận.",
  },
  {
    question: "Có cần bản dịch của người dịch được công nhận không?",
    answer:
      "Tuỳ nước tiếp nhận. Một số nước yêu cầu dịch viên được công nhận hoặc bản dịch có chứng nhận NAATI, một số chấp nhận bản dịch có xác nhận của Cục Lãnh sự.",
  },
  {
    question: "Vì sao hồ sơ bị trả lại dù giấy tờ đầy đủ?",
    answer:
      "Phổ biến nhất là sai thứ tự chứng nhận, thiếu bản dịch con dấu, tên viết không khớp và giấy tờ quá hạn.",
  },
  {
    question: "Có thể uỷ quyền cho người khác nộp thay không?",
    answer:
      "Được, nhưng văn bản uỷ quyền phải nêu rõ phạm vi gồm xin, nhận, chứng nhận và nộp hồ sơ.",
  },
];

export const Route = createFileRoute("/vi/blog/spouse-visa-document-checklist")({
  component: ViSpouseVisaChecklist,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "vi" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("spouse-visa-document-checklist", "vi") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "visa vợ chồng Thái Lan, giấy tờ kết hôn Thái Lan, dịch và chứng nhận giấy tờ Thái Lan, hồ sơ visa bạn đời",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "vi"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Trang chủ", url: "/vi" },
              { name: "Hướng dẫn", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("spouse-visa-document-checklist"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Visa & Immigration",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ d: string; note: string }> = [
  { d: "Giấy đăng ký kết hôn (คร.2) và giấy chứng nhận kết hôn (คร.3)", note: "Xin bản cấp mới từ cơ quan hộ tịch; cần dịch và chứng nhận" },
  { d: "Giấy khai sinh", note: "Dùng để đối chiếu danh tính và quan hệ gia đình" },
  { d: "Bản sao hộ khẩu (ทะเบียนบ้าน)", note: "Nên là bản cấp mới do cơ quan hộ tịch chứng thực" },
  { d: "Chứng minh nhân dân và hộ chiếu", note: "Cách viết tên phải khớp mọi giấy tờ trong bộ hồ sơ" },
  { d: "Lý lịch tư pháp (nếu yêu cầu)", note: "Thường có thời hạn ngắn; xin đúng thời điểm" },
  { d: "Giấy tờ chứng minh quan hệ và tài chính", note: "Theo danh mục của cơ quan tiếp nhận" },
];

const ORDER: string[] = [
  "Xin bản cấp mới của giấy tờ hộ tịch từ cơ quan hộ tịch",
  "Công chứng Notary cho các giấy tờ tư nhân (tuyên bố, uỷ quyền)",
  "Chứng nhận tại Cục Lãnh sự Bộ Ngoại giao khi được yêu cầu",
  "Dịch toàn văn kèm con dấu, tem và chú thích",
  "Hợp pháp hoá tại đại sứ quán nước tiếp nhận nếu cần",
  "Rà soát lần cuối: tên, ngày tháng, thời hạn và số lượng bản",
];

const MISTAKES: string[] = [
  "Dịch trước khi hoàn tất các lớp chứng nhận",
  "Xin giấy tờ hộ tịch quá sớm nên hết thời hạn khi nộp",
  "Tên viết không khớp giữa hộ chiếu, giấy kết hôn và bản dịch",
  "Thiếu bản dịch của con dấu và chú thích",
  "Không ghi chú việc kết hôn ở nước ngoài tại cơ quan hộ tịch Thái Lan khi được yêu cầu",
  "Uỷ quyền có phạm vi hẹp nên không nộp thay được",
];

function ViSpouseVisaChecklist() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/vi" className="text-muted-foreground hover:text-foreground">
          Trang chủ
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Hướng dẫn
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="spouse-visa-document-checklist" locale="vi" />
      <p className="answer-summary speakable-summary">
        Hồ sơ visa vợ/chồng bị chậm phần lớn vì thứ tự chứng nhận và thời hạn giấy tờ, không phải vì thiếu giấy tờ. Hãy
        lấy danh mục chính thức của cơ quan tiếp nhận, xin bản cấp mới của giấy tờ hộ tịch, rồi đi theo thứ tự “bản gốc →
        công chứng → Cục Lãnh sự → dịch → đại sứ quán”.
      </p>

      <h2>1. Danh mục giấy tờ thường dùng</h2>
      <ul>
        {DOCS.map((x) => (
          <li key={x.d}>
            <strong>{x.d}:</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>2. Thứ tự chứng nhận</h2>
      <ol>
        {ORDER.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Lỗi khiến hồ sơ bị trả lại</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. Câu hỏi thường gặp</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. Nguồn tham khảo và dịch vụ liên quan</h2>
      <p className="text-sm">
        Tham khảo:{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Cục Lãnh sự, Bộ Ngoại giao Thái Lan
        </a>{" "}
        ·{" "}
        <a href="https://www.bora.dopa.go.th/" target="_blank" rel="noreferrer">
          Cục Hành chính và Hộ tịch (Bộ Nội vụ Thái Lan)
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/vi/visa" className="text-primary underline">
            Dịch vụ hỗ trợ hồ sơ visa
          </Link>
        </li>
        <li>
          <Link to="/vi/marriage" className="text-primary underline">
            Đăng ký kết hôn và ghi chú hôn nhân
          </Link>
        </li>
        <li>
          <Link to="/vi/lgz" className="text-primary underline">
            Chứng nhận và hợp pháp hoá giấy tờ
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Nội dung rà soát ngày {PUBLISHED}. Đây là thông tin chung để chuẩn bị hồ sơ, không bảo đảm kết quả xét visa. Nếu
        bạn không muốn tự theo từng bước, đội ngũ hơn 15 năm kinh nghiệm của chúng tôi có thể tư vấn và thực hiện toàn bộ
        — liên hệ qua điện thoại, LINE hoặc email.
      </p>
    </article>
  );
}
